import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  ShieldCheckIcon,
  ListBulletIcon,
  PaintBrushIcon,
  SparklesIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { FormField, FieldOption, ValidationConfig } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';

interface FieldPropertiesProps {
  field?: FormField;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
}

const FieldProperties: React.FC<FieldPropertiesProps> = ({
  field,
  onUpdateField
}) => {
  const { t } = useLanguage();

  if (!field) {
    return (
      <div className="h-full flex items-center justify-center p-6 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="relative mb-6">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <SparklesIcon className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <Cog6ToothIcon className="h-3 w-3 text-white" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('formBuilder.selectField') || 'Select a Field'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xs">
            {t('formBuilder.selectFieldDescription') || 'Choose a field from the canvas to customize its properties and settings'}
          </p>
        </div>
      </div>
    );
  }

  const handleFieldUpdate = (updates: Partial<FormField>) => {
    onUpdateField(field.id, updates);
  };

  // Get available validation rules for the current field type
  const getAvailableValidationRules = (fieldType: string): string[] => {
    const baseRules = ['required'];
    
    switch (fieldType) {
      case 'text':
      case 'textarea':
        return [...baseRules, 'minLength', 'maxLength', 'pattern'];
      case 'email':
        return [...baseRules, 'email'];
      case 'number':
        return [...baseRules, 'min', 'max'];
      case 'date':
      case 'datetime':
        return [...baseRules, 'min', 'max'];
      case 'select':
      case 'multiselect':
      case 'radio':
      case 'checkbox':
        return baseRules;
      default:
        return baseRules;
    }
  };

  const addValidation = () => {
    const availableRules = getAvailableValidationRules(field.type);
    const newValidation: ValidationConfig = {
      rule: availableRules[0] as any || 'required',
      value: '',
      message: ''
    };
    
    const updatedValidation = [...(field.validations || []), newValidation];
    handleFieldUpdate({ validations: updatedValidation });
  };

  const updateValidation = (index: number, updates: Partial<ValidationConfig>) => {
    const updatedValidation = [...(field.validations || [])];
    updatedValidation[index] = { ...updatedValidation[index], ...updates };
    handleFieldUpdate({ validations: updatedValidation });
  };

  const removeValidation = (index: number) => {
    const updatedValidation = [...(field.validations || [])];
    updatedValidation.splice(index, 1);
    handleFieldUpdate({ validations: updatedValidation });
  };

  const addOption = () => {
    const newOption: FieldOption = {
      label: `Option ${(field.options?.length || 0) + 1}`,
      value: `option_${(field.options?.length || 0) + 1}`
    };
    
    const updatedOptions = [...(field.options || []), newOption];
    handleFieldUpdate({ options: updatedOptions });
  };

  const updateOption = (index: number, updates: Partial<FieldOption>) => {
    const updatedOptions = [...(field.options || [])];
    updatedOptions[index] = { ...updatedOptions[index], ...updates };
    handleFieldUpdate({ options: updatedOptions });
  };

  const removeOption = (index: number) => {
    const updatedOptions = [...(field.options || [])];
    updatedOptions.splice(index, 1);
    handleFieldUpdate({ options: updatedOptions });
  };

  const toggleOptionVisibility = (index: number) => {
    const updatedOptions = [...(field.options || [])];
    updatedOptions[index].hidden = !updatedOptions[index].hidden;
    handleFieldUpdate({ options: updatedOptions });
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="flex-shrink-0 p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <AdjustmentsHorizontalIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              {t('formBuilder.properties.title') || 'Field Properties'}
            </h3>
            <p className="text-blue-100 text-sm">
              {field.label || field.name}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium uppercase">
            {field.type.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-8 space-y-4">
        
        {/* General Properties Section */}
        <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
              <Cog6ToothIcon className="h-5 w-5 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('formBuilder.properties.general') || 'General Properties'}
            </h4>
          </div>
          
          <div className="space-y-3">
            {/* Label */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formBuilder.properties.label') || 'Label'}
              </label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => handleFieldUpdate({ label: e.target.value })}
                className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formBuilder.properties.name') || 'Field Name'}
              </label>
              <input
                type="text"
                value={field.name}
                onChange={(e) => handleFieldUpdate({ name: e.target.value })}
                className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Placeholder */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formBuilder.properties.placeholder') || 'Placeholder'}
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => handleFieldUpdate({ placeholder: e.target.value })}
                className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formBuilder.properties.description') || 'Description'}
              </label>
              <textarea
                value={field.description || ''}
                onChange={(e) => handleFieldUpdate({ description: e.target.value })}
                rows={2}
                className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              />
            </div>

            {/* Required */}
            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
              <input
                type="checkbox"
                id="required"
                checked={field.required || false}
                onChange={(e) => handleFieldUpdate({ required: e.target.checked })}
                className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
              />
              <label htmlFor="required" className="text-xs font-medium text-gray-900 dark:text-gray-100">
                {t('formBuilder.properties.required') || 'Required field'}
              </label>
            </div>
          </div>
        </div>

        {/* Validation Section */}
        <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <ShieldCheckIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formBuilder.properties.validation') || 'Validation Rules'}
              </h4>
            </div>
            <button
              onClick={addValidation}
              className="inline-flex items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-primary-500 transition-all"
            >
              <PlusIcon className="h-3 w-3 mr-1" />
              {t('formBuilder.properties.addValidation') || 'Add Rule'}
            </button>
          </div>

          {field.validations && field.validations.length > 0 ? (
            <div className="space-y-2">
              {field.validations.map((validation: any, index: number) => {
                const availableRules = getAvailableValidationRules(field.type);
                const isRuleCompatible = availableRules.includes(validation.rule);
                
                return (
                  <div key={index} className="p-3 bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start space-x-2">
                      {!isRuleCompatible && (
                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mt-1 flex-shrink-0" />
                      )}
                      <div className="flex-1 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('formBuilder.properties.validationRule') || 'Rule'}
                          </label>
                          <select
                            value={validation.rule}
                            onChange={(e) => updateValidation(index, { rule: e.target.value as any })}
                            className="form-select w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          >
                            {availableRules.map(rule => (
                              <option key={rule} value={rule}>
                                {t(`formBuilder.validation.${rule}`) || rule}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {validation.rule !== 'required' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('formBuilder.properties.validationValue') || 'Value'}
                            </label>
                            <input
                              type="text"
                              value={validation.value}
                              onChange={(e) => updateValidation(index, { value: e.target.value })}
                              className="form-input w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        )}
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('formBuilder.properties.validationMessage') || 'Error Message'}
                          </label>
                          <input
                            type="text"
                            value={validation.message}
                            onChange={(e) => updateValidation(index, { message: e.target.value })}
                            className="form-input w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeValidation(index)}
                        className="p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <ShieldCheckIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {t('formBuilder.validationHelpText') || 'No validation rules set. Add rules to validate user input.'}
              </p>
            </div>
          )}
        </div>

        {/* File Configuration Section - Only for file and image */}
        {['file', 'image'].includes(field.type) && (
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <DocumentArrowUpIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formBuilder.properties.fileSettings') || 'File Settings'}
              </h4>
            </div>
            
            <div className="space-y-3">
              {/* Multiple Files */}
              <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                <input
                  type="checkbox"
                  id="multiple"
                  checked={field.config?.multiple || false}
                  onChange={(e) => handleFieldUpdate({ 
                    config: { 
                      ...field.config, 
                      multiple: e.target.checked 
                    } 
                  })}
                  className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
                />
                <label htmlFor="multiple" className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  {t('formBuilder.properties.multipleFiles') || 'Allow multiple files'}
                </label>
              </div>

              {/* Max File Size */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('formBuilder.properties.maxFileSize') || 'Max File Size (MB)'}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={field.config?.maxSize ? Math.round(field.config.maxSize / (1024 * 1024)) : 5}
                  onChange={(e) => handleFieldUpdate({ 
                    config: { 
                      ...field.config, 
                      maxSize: parseInt(e.target.value) * 1024 * 1024 
                    } 
                  })}
                  className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>

              {/* Accepted File Types */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('formBuilder.properties.acceptedTypes') || 'Accepted File Types'}
                </label>
                <input
                  type="text"
                  value={field.config?.accept || (field.type === 'image' ? 'image/*' : '*/*')}
                  onChange={(e) => handleFieldUpdate({ 
                    config: { 
                      ...field.config, 
                      accept: e.target.value 
                    } 
                  })}
                  placeholder={field.type === 'image' ? 'image/*' : '.pdf,.doc,.docx'}
                  className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('formBuilder.properties.acceptedTypesHelp') || 'Use MIME types (e.g., image/*) or file extensions (e.g., .pdf,.doc)'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Options Section - Only for select, multiselect, radio, checkbox */}
        {['select', 'multiselect', 'radio', 'checkbox'].includes(field.type) && (
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <ListBulletIcon className="h-5 w-5 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('formBuilder.properties.options') || 'Options'}
                </h4>
              </div>
              <button
                onClick={addOption}
                className="inline-flex items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-primary-500 transition-all"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                {t('formBuilder.properties.addOption') || 'Add Option'}
              </button>
            </div>

            {field.options && field.options.length > 0 ? (
              <div className="space-y-2">
                {field.options.map((option, index) => (
                  <div key={index} className="p-3 bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start space-x-2">
                      <div className="flex-1 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('formBuilder.properties.optionLabel') || 'Label'}
                          </label>
                          <input
                            type="text"
                            value={option.label}
                            onChange={(e) => updateOption(index, { label: e.target.value })}
                            className="form-input w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('formBuilder.properties.optionValue') || 'Value'}
                          </label>
                          <input
                            type="text"
                            value={option.value}
                            onChange={(e) => updateOption(index, { value: e.target.value })}
                            className="form-input w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-center space-y-1 pt-1">
                        <button
                          onClick={() => toggleOptionVisibility(index)}
                          className={`p-1.5 rounded-md transition-colors ${
                            option.hidden
                              ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                              : 'text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300'
                          }`}
                        >
                          {option.hidden ? (
                            <EyeSlashIcon className="h-3.5 w-3.5" />
                          ) : (
                            <EyeIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => removeOption(index)}
                          className="p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <ListBulletIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {t('formBuilder.properties.noOptions') || 'No options added yet. Click "Add Option" to get started.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Appearance Section */}
        <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg flex items-center justify-center">
              <PaintBrushIcon className="h-5 w-5 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('formBuilder.properties.appearance') || 'Appearance'}
            </h4>
          </div>
          
          <div className="space-y-3">
            {/* Width */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formBuilder.properties.width') || 'Width'}
              </label>
              <select
                value={field.width || 'full'}
                onChange={(e) => handleFieldUpdate({ width: e.target.value as any })}
                className="form-select w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="full">{t('formBuilder.properties.widthFull') || 'Full Width'}</option>
                <option value="half">{t('formBuilder.properties.widthHalf') || 'Half Width'}</option>
                <option value="third">{t('formBuilder.properties.widthThird') || 'Third Width'}</option>
                <option value="quarter">{t('formBuilder.properties.widthQuarter') || 'Quarter Width'}</option>
              </select>
            </div>

            {/* Hidden */}
            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
              <input
                type="checkbox"
                id="hidden"
                checked={field.hidden || false}
                onChange={(e) => handleFieldUpdate({ hidden: e.target.checked })}
                className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
              />
              <label htmlFor="hidden" className="text-xs font-medium text-gray-900 dark:text-gray-100">
                {t('formBuilder.properties.hidden') || 'Hidden field'}
              </label>
            </div>
          </div>
        </div>

        {/* Bottom spacer to ensure last items are fully visible */}
        <div className="h-12"></div>
      </div>
    </div>
  );
};

export default FieldProperties;